import { useEffect, useMemo, useState } from "react";
import { Link, navigate } from "@/lib/router";
import { Public } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronRight, TrendingUp, FileSearch, Building2, Award,
  Search, Bell, MapPin, CalendarDays, ArrowRight, Sparkles,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export function HomePage() {
  const [cats, setCats] = useState<any[]>([]);
  const [recent, setRecent] = useState<Record<string, any[] | null>>({});
  const [latest, setLatest] = useState<any[] | null>(null);
  const [winners, setWinners] = useState<any[] | null>(null);
  const [stats, setStats] = useState<{ total: number; winners: number; today: number; categories: number } | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    Public.categories().then(async (cs) => {
      setCats(cs);
      const top = cs.filter((c: any) => !c.parent_id).slice(0, 6);
      const map: Record<string, any[]> = {};
      await Promise.all(top.map(async (c: any) => {
        const r = await Public.projects({ category: c.slug, page_size: 6 }).catch(() => ({ items: [] as any[] }));
        map[c.slug] = (r as any).items || [];
      }));
      setRecent(map);
    });
    Public.projects({ page_size: 10 }).then((r) => setLatest(r.items || [])).catch(() => setLatest([]));
    Public.projects({ page_size: 8, "f.type": "中标公告" } as any)
      .then((r) => setWinners(r.items || []))
      .catch(() => setWinners([]));
    Public.siteStats().then(setStats).catch(() => setStats(null));
  }, []);

  const top = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate("/search", { q: q.trim() });
  };

  return (
    <div className="space-y-8 md:space-y-12 pb-12 md:pb-16">
      {/* HERO */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,hsl(var(--primary)/0.18),transparent_55%),radial-gradient(circle_at_80%_30%,hsl(var(--primary)/0.12),transparent_60%)]" />
        <div className="container mx-auto px-4 pt-10 md:pt-24 pb-10 md:pb-20">
          <div className="max-w-4xl mx-auto text-center space-y-5 md:space-y-6">
            <Badge variant="secondary" className="rounded-full inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> 每日实时聚合 · 全国覆盖
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight leading-[1.15]">
              全国招投中标信息<br className="hidden sm:inline" />
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">一站式检索平台</span>
            </h1>
            <p className="text-muted-foreground text-sm md:text-lg leading-relaxed max-w-2xl mx-auto px-2">
              汇聚国家电网、电投、华能、华电、南网、能建等主流招标方的招标采购公告、中标候选人公示、中标结果。
            </p>

            <form onSubmit={onSearch} className="mx-auto max-w-2xl pt-2">
              <div className="relative flex items-center rounded-full border bg-background shadow-lg shadow-primary/5 focus-within:ring-2 focus-within:ring-primary/30 transition">
                <Search className="absolute left-4 md:left-5 h-4 md:h-5 w-4 md:w-5 text-muted-foreground" />
                <Input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="输入关键词…"
                  className="border-0 pl-11 md:pl-12 pr-24 md:pr-32 h-12 md:h-14 text-sm md:text-base bg-transparent focus-visible:ring-0"
                />
                <Button type="submit" className="absolute right-1.5 rounded-full h-9 md:h-11 px-4 md:px-6 text-sm">
                  <span className="hidden sm:inline">搜索</span> <ArrowRight className="h-4 w-4 sm:ml-1" />
                </Button>
              </div>
              <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 pt-3 text-xs text-muted-foreground">
                <span>热门：</span>
                {["国家电网", "南方电网", "华能集团", "光伏EPC", "变压器"].map((t) => (
                  <button key={t} type="button" onClick={() => { setQ(t); navigate("/search", { q: t }); }} className="hover:text-primary">
                    #{t}
                  </button>
                ))}
              </div>
            </form>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 pt-6 md:pt-8 max-w-3xl mx-auto">
              <Stat icon={<FileSearch />} label="累计公告" value={stats ? fmt(stats.total) : "—"} />
              <Stat icon={<Building2 />} label="覆盖招标方" value={`${stats?.categories ?? top.length ?? 0}+`} />
              <Stat icon={<Award />} label="中标快讯" value={stats ? fmt(stats.winners) : "—"} />
              <Stat icon={<TrendingUp />} label="今日更新" value={stats ? fmt(stats.today) : "—"} />
            </div>
          </div>
        </div>
      </section>

      {/* 栏目快速入口 */}
      <section className="container mx-auto px-4">
        <SectionTitle title="栏目导航" subtitle="按招标方分类浏览公告与中标信息" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {top.slice(0, 12).map((c) => (
            <Link key={c.id} to={`/c/${c.slug}`}
              className="group rounded-xl border bg-card hover:border-primary/40 hover:shadow-md transition p-4 flex flex-col items-start gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Building2 className="h-4 w-4" />
              </span>
              <div className="text-sm font-medium group-hover:text-primary transition">{c.name}</div>
              <div className="text-xs text-muted-foreground line-clamp-1">
                {c.summary || "招标公告 / 中标公示"}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新公告 + 中标快讯 */}
      <section className="container mx-auto px-4 grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 min-w-0 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" /> 最新公告
            </CardTitle>
            <Link to="/search" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center">
              查看全部 <ChevronRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y">
              {latest === null && Array.from({ length: 6 }, (_, i) => (
                <li key={`s-${i}`} className="py-2.5 flex items-center gap-3">
                  <Skeleton className="h-3 w-[70%]" />
                  <Skeleton className="h-3 w-16 ml-auto shrink-0" />
                </li>
              ))}
              {(latest || []).slice(0, 8).map((p) => (
                <li key={p.id} className="py-2.5 flex items-center gap-3 min-w-0">
                  <Link to={`/p/${p.slug}`} className="text-sm hover:text-primary flex-1 min-w-0 inline-flex items-center gap-2">
                    {p.locked
                      ? <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 h-5 border-amber-500/50 text-amber-700">VIP</Badge>
                      : <span className="shrink-0 inline-block h-1.5 w-1.5 rounded-full bg-primary/50" />}
                    <span className="truncate">{p.title}</span>
                  </Link>
                  {p.dynamic_fields?.province && (
                    <span className="text-xs text-muted-foreground hidden md:inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" />{p.dynamic_fields.province}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground shrink-0 inline-flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}
                  </span>
                </li>
              ))}
              {latest && latest.length === 0 && <li className="py-10 text-center text-sm text-muted-foreground">暂无数据</li>}
            </ul>
          </CardContent>
        </Card>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="h-4 w-4 text-amber-500" /> 中标快讯
            </CardTitle>
            <Link to="/winners" className="text-xs text-muted-foreground hover:text-amber-600 inline-flex items-center">
              查看全部 <ChevronRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-3">
              {winners === null && Array.from({ length: 4 }, (_, i) => (
                <li key={`ws-${i}`} className="border-l-2 border-amber-500/30 pl-3 space-y-1.5">
                  <Skeleton className="h-3.5 w-[80%]" />
                  <Skeleton className="h-3 w-[55%]" />
                </li>
              ))}
              {(winners || []).slice(0, 6).map((p) => (
                <li key={p.id} className="border-l-2 border-amber-500/40 pl-3 min-w-0">
                  <Link to={`/p/${p.slug}`} className="text-sm font-medium hover:text-primary line-clamp-2 break-words">{p.title}</Link>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-2 break-all">
                    {p.dynamic_fields?.bidder && <span>中标人：{p.dynamic_fields.bidder}</span>}
                    {p.dynamic_fields?.amount && <span>金额：{p.dynamic_fields.amount}</span>}
                  </div>
                </li>
              ))}
              {winners && winners.length === 0 && <li className="py-8 text-center text-sm text-muted-foreground">暂无数据</li>}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* 各栏目最新 */}
      <section className="container mx-auto px-4">
        <SectionTitle title="按栏目浏览" subtitle="国央企招标方最新动态" />
        <div className="grid lg:grid-cols-2 gap-6">
          {top.slice(0, 6).map((c) => (
            <Card key={c.id} className="overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {c.name}
                </CardTitle>
                <Link to={`/c/${c.slug}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center">
                  更多 <ChevronRight className="h-3 w-3" />
                </Link>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="divide-y">
                  {recent[c.slug] === undefined || recent[c.slug] === null ? (
                    Array.from({ length: 4 }, (_, i) => (
                      <li key={`rs-${c.slug}-${i}`} className="py-2.5 flex items-center gap-3">
                        <Skeleton className="h-3 w-[70%]" />
                        <Skeleton className="h-3 w-14 ml-auto shrink-0" />
                      </li>
                    ))
                  ) : recent[c.slug]!.length === 0 ? (
                    <li className="py-6 text-center text-sm text-muted-foreground">暂无数据</li>
                  ) : (
                    recent[c.slug]!.slice(0, 6).map((p) => (
                      <li key={p.id} className="py-2.5 flex items-center justify-between gap-3 min-w-0">
                        <Link to={`/p/${p.slug}`} className="text-sm hover:text-primary flex-1 min-w-0 inline-flex items-center gap-2">
                          {p.locked
                            ? <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 h-5 border-amber-500/50 text-amber-700">VIP</Badge>
                            : <span className="shrink-0 inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />}
                          <span className="truncate">{p.title}</span>
                        </Link>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function fmt(n: number) {
  if (!n || n < 1000) return String(n || "—");
  if (n < 10000) return n.toLocaleString();
  return `${(n / 10000).toFixed(1)}万`;
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-5 flex items-end justify-between">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card/60 backdrop-blur px-4 py-3 text-left">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary [&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-base md:text-lg font-bold">{value}</div>
      </div>
    </div>
  );
}
